import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Wallet, 
  Download, 
  Key, 
  AlertTriangle,
  Copy,
  ArrowDownToLine,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminCustodialWalletsPage = () => {
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState([]);
  const [treasuryAddress, setTreasuryAddress] = useState('');
  const [sweeping, setSweeping] = useState(false);
  const [selectedWallets, setSelectedWallets] = useState([]);
  const [showMasterKey, setShowMasterKey] = useState(false);
  const [masterKey, setMasterKey] = useState(null);
  const [exportedKeys, setExportedKeys] = useState({});

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blockchain/custodial-wallets`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWallets(data.wallets || []);
      }
    } catch (error) {
      console.error('Failed to fetch wallets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSweep = async () => {
    if (!treasuryAddress) {
      return toast.error('Enter treasury address');
    }
    
    if (!window.confirm(`Sweep funds from ${selectedWallets.length || 'ALL'} wallets to ${treasuryAddress}?`)) {
      return;
    }
    
    setSweeping(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/blockchain/sweep`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          treasury_address: treasuryAddress,
          chain: 'polygon',
          user_ids: selectedWallets.length > 0 ? selectedWallets : undefined,
          min_balance: 0.01
        })
      });
      
      if (!response.ok) {
        let errorMessage = 'Sweep failed';
        try {
          const error = await response.json();
          errorMessage = error.detail || error.message || errorMessage;
        } catch (e) {
          // Response is not JSON, try to get text
          const text = await response.text();
          errorMessage = text || `Sweep failed with status ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      toast.success(`Swept ${data.total_swept.toFixed(4)} MATIC from ${data.wallets_swept.length} wallets`);
      fetchWallets();
      setSelectedWallets([]);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSweeping(false);
    }
  };

  const exportPrivateKey = async (userId, address) => {
    if (!window.confirm('⚠️ WARNING: Export private key? This is sensitive data!')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/blockchain/export-private-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId })
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const data = await response.json();
      setExportedKeys(prev => ({ ...prev, [userId]: data.private_key }));
      toast.success('Private key exported');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getMasterSeed = async () => {
    if (!window.confirm('🔴 CRITICAL: Export master encryption key? This decrypts ALL user keys!')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/blockchain/master-seed`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to get master seed');
      }
      
      const data = await response.json();
      setMasterKey(data.encryption_key);
      setShowMasterKey(true);
      toast.success('Master key retrieved - KEEP SECURE!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  if (loading) {
    return (
      <AdminLayout title="Custodial Wallets">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-lg">Loading wallets...</div>
        </div>
      </AdminLayout>
    );
  }

  const totalBalance = wallets.reduce((sum, w) => sum + w.total_value_estimate, 0);

  return (
    <AdminLayout title="Custodial Wallets">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Custodial Wallet Management
          </h1>
          <p className="text-muted-foreground">
            View user crypto wallets, sweep funds to treasury, export keys for backup
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Wallets</p>
                  <h3 className="text-3xl font-bold">{wallets.length}</h3>
                </div>
                <Wallet className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Est. Total Value</p>
                  <h3 className="text-3xl font-bold">${totalBalance.toFixed(2)}</h3>
                </div>
                <ArrowDownToLine className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Selected</p>
                  <h3 className="text-3xl font-bold">{selectedWallets.length}</h3>
                </div>
                <Download className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sweep Tool */}
        <Card className="border-2 border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Sweep Funds to Treasury
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Treasury Address (Polygon)</label>
                <Input
                  placeholder="0x..."
                  value={treasuryAddress}
                  onChange={(e) => setTreasuryAddress(e.target.value)}
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleSweep}
                  disabled={sweeping || !treasuryAddress}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {sweeping ? 'Sweeping...' : `Sweep ${selectedWallets.length > 0 ? selectedWallets.length : 'All'} Wallet${selectedWallets.length === 1 ? '' : 's'}`}
                </Button>
                
                {wallets.length > 0 && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      if (selectedWallets.length === wallets.length) {
                        setSelectedWallets([]);
                      } else {
                        setSelectedWallets(wallets.map(w => w.user_id));
                      }
                    }}
                  >
                    {selectedWallets.length === wallets.length ? 'Deselect All' : 'Select All'} ({wallets.length})
                  </Button>
                )}
                
                {selectedWallets.length > 0 && selectedWallets.length < wallets.length && (
                  <Button variant="ghost" size="sm" onClick={() => setSelectedWallets([])}>
                    Clear ({selectedWallets.length})
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                💡 Sweeps MATIC from Polygon network. Deducts gas automatically. Min balance: 0.01 MATIC
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Master Key */}
        <Card className="border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-red-600" />
              Master Encryption Key (Backup)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {!showMasterKey ? (
                <Button
                  onClick={getMasterSeed}
                  variant="destructive"
                >
                  <Key className="h-4 w-4 mr-2" />
                  Reveal Master Key
                </Button>
              ) : (
                <div className="space-y-2">
                  <div className="p-4 bg-background rounded-lg border-2 border-red-300 dark:border-red-700">
                    <p className="text-xs text-muted-foreground mb-2">MASTER ENCRYPTION KEY</p>
                    <code className="text-sm font-mono break-all block">{masterKey}</code>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      onClick={() => copyToClipboard(masterKey, 'Master key')}
                    >
                      <Copy className="h-3 w-3 mr-1" /> Copy
                    </Button>
                  </div>
                  <p className="text-xs text-red-600 dark:text-red-400">
                    🔴 This key decrypts ALL user private keys. Store in cold storage. Never share.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Wallets List */}
        <Card>
          <CardHeader>
            <CardTitle>User Wallets ({wallets.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {wallets.map(wallet => (
                <div key={wallet.user_id} className="p-4 border rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <input
                          type="checkbox"
                          checked={selectedWallets.includes(wallet.user_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedWallets([...selectedWallets, wallet.user_id]);
                            } else {
                              setSelectedWallets(selectedWallets.filter(id => id !== wallet.user_id));
                            }
                          }}
                          className="rounded"
                        />
                        <div>
                          <p className="font-semibold">{wallet.user_email}</p>
                          <p className="text-xs text-muted-foreground font-mono">{wallet.address}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-3">
                        {Object.entries(wallet.balances).map(([chain, bal]) => (
                          <div key={chain} className="text-sm">
                            <span className="text-muted-foreground">{bal.symbol}:</span>{' '}
                            <span className="font-mono font-semibold">{bal.balance.toFixed(4)}</span>
                          </div>
                        ))}
                      </div>

                      {exportedKeys[wallet.user_id] && (
                        <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
                          <p className="text-xs text-yellow-800 dark:text-yellow-300 mb-1">Private Key:</p>
                          <code className="text-xs font-mono break-all block">{exportedKeys[wallet.user_id]}</code>
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={() => copyToClipboard(exportedKeys[wallet.user_id], 'Private key')}
                          >
                            <Copy className="h-3 w-3 mr-1" /> Copy
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => exportPrivateKey(wallet.user_id, wallet.address)}
                      >
                        <Key className="h-4 w-4 mr-1" />
                        Export Key
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};
